import React, { useState, useEffect, useRef } from 'react';
import {
  Box,
  Typography,
  Button,
  IconButton,
  TextField,
  Divider,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Grid,
  Autocomplete,
  Alert,
  CircularProgress,
  Paper,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Checkbox,
  FormControlLabel,
  FormGroup,
  InputAdornment,
  Tooltip,
  Stack,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import {
  Close as CloseIcon,
  FavoriteBorder as HeartIcon,
  Thermostat as TempIcon,
  MonitorWeight as WeightIcon,
  Height as HeightIcon,
  Air as LungIcon,
  Speed as SpeedIcon,
  Bloodtype as BloodIcon,
  AutoAwesome as AiIcon,
  ExpandMore as ExpandMoreAccordionIcon,
  NoteAdd as NoteAddIcon,
  Print as PrintIcon,
  Layers as LayersIcon,
} from '@mui/icons-material';
import Snackbar from '@mui/material/Snackbar';
import axios from '../config/axios';
import { getApiUrl } from '../config/environment';
import { useAuth } from '../contexts/AuthContext';
import { getLoggedInProfessionalOption, mergeLoggedInProfessionalIntoOptions } from '../utils/loggedInProfessional';
import { sanitizeClinicalHtml } from '../utils/clinicalHtml';
import { resolveEvolutionEntryFormat } from '../utils/clinicEvolutionFormat';
import { printClinicalEvolutionFullDocument } from '../utils/clinicalEvolutionFullPrint';
import { buildCertificatePrintHtml } from '../utils/certificatePrintDocument';
import { buildPrescriptionPrintHtml } from '../utils/prescriptionPrintDocument';
import EvolutionPrintablesWizard from './evolution/EvolutionPrintablesWizard';
import EvolutionIndicationsDialog from './evolution/EvolutionIndicationsDialog';
import { buildEvolutionPrintBatch, evolutionHasPrintBatchContent } from './evolution/mergeEvolutionPrintBatch';
import { evolutionPrintableListLabel } from './evolution/evolutionViewHelpers';
import {
  defaultEvolutionReturnWorkflow,
  type EvolutionAttachedPrintable,
  type EvolutionPrintableCategory,
  type EvolutionReturnWorkflow,
} from './evolution/evolutionWorkflowTypes';
import {
  buildClinicPrintDocumentHtml,
  buildClinicMultiSheetPrintHtml,
  buildEvolutionPrintAttachmentSection,
  escapeHtml,
} from '../utils/clinicPrintLetterhead';

interface VitalSigns {
  blood_pressure_systolic?: number;
  blood_pressure_diastolic?: number;
  heart_rate?: number;
  respiratory_rate?: number;
  temperature?: number;
  oxygen_saturation?: number;
  weight?: number;
  height?: number;
  bmi?: number;
  pain_scale?: number;
  blood_glucose?: number;
}

const emptyVitals: VitalSigns = {};

const emptyForm = () => ({
  patient_id: '',
  professional_id: '',
  appointment_id: '',
  evolution_date: new Date().toISOString().split('T')[0],
  evolution_time: new Date().toTimeString().slice(0, 5),
  evolution_entry_format: 'free_text' as const,
  evolution_free_text: '',
  subjective: '',
  objective: '',
  assessment: '',
  plan: '',
  vital_signs: { ...emptyVitals },
  diagnosis: '',
  diagnosis_code: '',
  procedures_performed: [] as any[],
  internal_notes: '',
  follow_up_notes: '',
  follow_up_date: '',
  metadata: {} as Record<string, unknown>,
});

const toArray = (value: unknown): unknown[] => (Array.isArray(value) ? value : []);

export interface HealthcareEvolutionDialogProps {
  open: boolean;
  onClose: () => void;
  patient: { id: string; full_name: string } | null;
  onSaved?: () => void;
  onError?: (message: string) => void;
  /** Quando definido (ex.: abrir pela timeline na agenda), carrega PUT em vez de POST. */
  existingEvolutionId?: string | null;
  /**
   * Painel inline (ex.: teleconsulta profissional): sem portal, sem backdrop, ocupa o container pai.
   */
  embedded?: boolean;
}

const HealthcareEvolutionDialog: React.FC<HealthcareEvolutionDialogProps> = ({
  open,
  onClose,
  patient,
  onSaved,
  onError,
  existingEvolutionId = null,
  embedded = false,
}) => {
  const { user } = useAuth();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const [form, setForm] = useState<any>(emptyForm());
  const [saving, setSaving] = useState(false);
  const [profSearch, setProfSearch] = useState('');
  const [profOptions, setProfOptions] = useState<any[]>([]);
  const lastProfRef = useRef('');

  const [printWizardOpen, setPrintWizardOpen] = useState(false);
  const [indicationsDialogOpen, setIndicationsDialogOpen] = useState(false);
  const [attachedPrintables, setAttachedPrintables] = useState<EvolutionAttachedPrintable[]>([]);
  const [returnWorkflow, setReturnWorkflow] = useState<EvolutionReturnWorkflow>(() =>
    defaultEvolutionReturnWorkflow(),
  );
  const [procedureLabels, setProcedureLabels] = useState<string[]>([]);

  const [loadingExisting, setLoadingExisting] = useState(false);
  const [loadedEvolutionSnapshot, setLoadedEvolutionSnapshot] = useState<any | null>(null);
  const [signing, setSigning] = useState(false);

  const isSignedState = Boolean(
    loadedEvolutionSnapshot && (loadedEvolutionSnapshot.status === 'signed' || loadedEvolutionSnapshot.is_signed),
  );
  const contentDisabled = loadingExisting || isSignedState;

  const [aiDialogOpen, setAiDialogOpen] = useState(false);
  const [aiComplaint, setAiComplaint] = useState('');
  const [aiMedicalHistory, setAiMedicalHistory] = useState('');
  const [aiGenerating, setAiGenerating] = useState(false);
  const [aiError, setAiError] = useState('');
  const [aiSnackbar, setAiSnackbar] = useState({ open: false, message: '', severity: 'success' as 'success' | 'error' });

  const searchProfessionals = async (q: string) => {
    try {
      const { data } = await axios.get(getApiUrl('/clinic/prescriptions-search/professionals'), { params: { search: q } });
      const rows = toArray(data?.data ?? data) as any[];
      setProfOptions(mergeLoggedInProfessionalIntoOptions(rows, user));
    } catch {
      const me = getLoggedInProfessionalOption(user);
      setProfOptions(me ? [me] : []);
    }
  };

  useEffect(() => {
    if (!open || !patient?.id) return;

    if (!existingEvolutionId) {
      const me = getLoggedInProfessionalOption(user);
      setForm({
        ...emptyForm(),
        patient_id: patient.id,
        ...(me ? { professional_id: me.id } : {}),
      });
      setProfSearch(me?.full_name || '');
      searchProfessionals('');
      setAttachedPrintables([]);
      setReturnWorkflow(defaultEvolutionReturnWorkflow());
      setProcedureLabels([]);
      setLoadedEvolutionSnapshot(null);
      setLoadingExisting(false);
      return;
    }

    let cancelled = false;
    setLoadingExisting(true);
    setLoadedEvolutionSnapshot(null);
    void (async () => {
      try {
        const { data: evo } = await axios.get(getApiUrl(`/clinic/evolutions/${existingEvolutionId}`));
        if (cancelled) return;
        if (evo.patient_id && evo.patient_id !== patient.id) {
          onError?.('Esta evolução pertence a outro paciente.');
          setLoadingExisting(false);
          return;
        }
        const mergedFreeText = (() => {
          const existing = String(evo.evolution_free_text ?? '').trim();
          if (existing) return evo.evolution_free_text ?? '';
          if (resolveEvolutionEntryFormat(evo) === 'free_text') return evo.evolution_free_text ?? '';
          const parts: string[] = [];
          if (evo.subjective?.trim()) parts.push(`Subjetivo\n${evo.subjective.trim()}`);
          if (evo.objective?.trim()) parts.push(`Objetivo\n${evo.objective.trim()}`);
          if (evo.assessment?.trim()) parts.push(`Avaliação\n${evo.assessment.trim()}`);
          if (evo.plan?.trim()) parts.push(`Plano\n${evo.plan.trim()}`);
          return parts.join('\n\n');
        })();

        setForm({
          ...emptyForm(),
          ...evo,
          evolution_entry_format: 'free_text',
          evolution_free_text: mergedFreeText,
          subjective: '',
          objective: '',
          assessment: '',
          plan: '',
          vital_signs: evo.vital_signs || { ...emptyVitals },
          evolution_date: evo.evolution_date?.split('T')[0] || '',
          follow_up_date: evo.follow_up_date?.split('T')[0] || '',
          appointment_id: '',
          patient_id: patient.id,
        });

        const wf = evo.metadata?.evolution_workflow;
        if (wf?.printables?.length) setAttachedPrintables(wf.printables);
        else setAttachedPrintables([]);
        if (wf?.return) setReturnWorkflow({ ...defaultEvolutionReturnWorkflow(), ...wf.return });
        else setReturnWorkflow(defaultEvolutionReturnWorkflow());
        const pp = evo.procedures_performed || [];
        const labels = Array.isArray(pp) ? pp.map((p: any) => p?.name || p?.label).filter(Boolean) : [];
        setProcedureLabels(labels);

        if (evo.professional?.id) {
          setProfOptions(mergeLoggedInProfessionalIntoOptions([evo.professional], user));
          setProfSearch(evo.professional.full_name || '');
        } else {
          await searchProfessionals('');
          setProfSearch('');
        }

        setLoadedEvolutionSnapshot(evo);
      } catch (e: any) {
        console.error(e);
        onError?.(e?.response?.data?.error || 'Erro ao carregar evolução');
      } finally {
        if (!cancelled) setLoadingExisting(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [open, patient?.id, existingEvolutionId]);

  useEffect(() => {
    if (!open) return;
    if (profSearch === lastProfRef.current) return;
    const t = setTimeout(() => {
      lastProfRef.current = profSearch;
      searchProfessionals(profSearch);
    }, 300);
    return () => clearTimeout(t);
  }, [profSearch, open]);

  const updateVital = (field: string, value: string) => {
    const num = value === '' ? undefined : parseFloat(value);
    setForm((prev: any) => ({ ...prev, vital_signs: { ...prev.vital_signs, [field]: num } }));
  };

  const printableCategoryLabel: Record<EvolutionPrintableCategory, string> = {
    prescription: 'Receituário',
    certificate: 'Atestado',
    exam_order: 'Exames',
    indication: 'Indicações',
    report: 'Laudo',
    surgical_risk: 'Risco cirúrgico',
  };

  const printHtmlInNewWindow = (html: string) => {
    const w = window.open('', '_blank');
    if (w) {
      w.document.write(html);
      w.document.close();
      setTimeout(() => w.print(), 400);
    }
  };

  const extractBodyInner = (fullHtml: string) => {
    const m = fullHtml.match(/<body[^>]*>([\s\S]*)<\/body>/i);
    return m ? m[1].trim() : fullHtml;
  };

  const printAttachedDocuments = async (items: EvolutionAttachedPrintable[]) => {
    if (!items.length) return;
    let clinic: any = null;
    try {
      const { data } = await axios.get(getApiUrl('/clinic/settings'));
      clinic = data?.data ?? null;
    } catch {
      /* ignore */
    }

    const parts: string[] = [];
    for (const p of items) {
      if (p.linkedDocumentId && p.linkedDocumentType === 'prescription') {
        try {
          const { data } = await axios.get(getApiUrl(`/clinic/prescriptions/${p.linkedDocumentId}/print`));
          const printData = data?.data ?? data;
          parts.push(
            `<div class="attachment-block attachment-block--embedded">${extractBodyInner(buildPrescriptionPrintHtml(printData))}</div>`,
          );
        } catch {
          const body = sanitizeClinicalHtml(String(p.bodyText || '').replace(/\r\n|\r|\n/g, '<br/>'));
          const cat = printableCategoryLabel[p.category] || p.category;
          parts.push(buildEvolutionPrintAttachmentSection(cat, p.templateName, body));
        }
      } else if (p.linkedDocumentId && p.linkedDocumentType === 'certificate') {
        try {
          const { data } = await axios.get(getApiUrl(`/clinic/certificates/${p.linkedDocumentId}/print`));
          const printData = data?.data ?? data;
          parts.push(
            `<div class="attachment-block attachment-block--embedded">${extractBodyInner(buildCertificatePrintHtml(printData))}</div>`,
          );
        } catch {
          const body = sanitizeClinicalHtml(String(p.bodyText || '').replace(/\r\n|\r|\n/g, '<br/>'));
          const cat = printableCategoryLabel[p.category] || p.category;
          parts.push(buildEvolutionPrintAttachmentSection(cat, p.templateName, body));
        }
      } else {
        const body = sanitizeClinicalHtml(String(p.bodyText || '').replace(/\r\n|\r|\n/g, '<br/>'));
        const cat = printableCategoryLabel[p.category] || p.category;
        parts.push(buildEvolutionPrintAttachmentSection(cat, p.templateName, body));
      }
    }

    const sheets = parts.map((bodyHtml) => ({ bodyHtml }));
    printHtmlInNewWindow(
      buildClinicMultiSheetPrintHtml({
        clinic,
        browserTitle: 'Impressão',
        sheets,
      }),
    );
  };

  const printProcedureLabel = async (name: string) => {
    let clinic: any = null;
    try {
      const { data } = await axios.get(getApiUrl('/clinic/settings'));
      clinic = data?.data ?? null;
    } catch {
      /* ignore */
    }
    const section = buildEvolutionPrintAttachmentSection(
      'Indicações',
      'Procedimento indicado',
      `<p style="margin:0">${escapeHtml(name)}</p>`,
    );
    printHtmlInNewWindow(
      buildClinicPrintDocumentHtml({
        clinic,
        browserTitle: name.slice(0, 120),
        badgeText: 'Indicação',
        bodyHtml: section,
        footerNote: 'Documento emitido pelo sistema da clínica.',
      }),
    );
  };

  const parseAiEvolution = (text: string) => {
    const result = {
      subjective: '',
      objective: '',
      assessment: '',
      plan: '',
      diagnosis: '',
      diagnosis_code: '',
      internal_notes: '',
      follow_up_notes: '',
    };
    const lines = text.split('\n').map((l) => l.trim());
    let current: keyof typeof result | null = null;
    for (const line of lines) {
      const lower = line.toLowerCase();
      if (lower.startsWith('subjetivo:') || lower.startsWith('s:') || lower.startsWith('subjective:') || lower === 's' || lower === 'subjetivo') {
        current = 'subjective';
        const r = line.split(':').slice(1).join(':').trim();
        if (r) result.subjective = r;
        continue;
      }
      if (lower.startsWith('objetivo:') || lower.startsWith('o:') || lower.startsWith('objective:') || lower === 'o' || lower === 'objetivo') {
        current = 'objective';
        const r = line.split(':').slice(1).join(':').trim();
        if (r) result.objective = r;
        continue;
      }
      if (
        lower.startsWith('avaliação:') ||
        lower.startsWith('avaliacao:') ||
        lower.startsWith('a:') ||
        lower.startsWith('assessment:') ||
        lower === 'a' ||
        lower === 'avaliação'
      ) {
        current = 'assessment';
        const r = line.split(':').slice(1).join(':').trim();
        if (r) result.assessment = r;
        continue;
      }
      if (lower.startsWith('plano:') || lower.startsWith('p:') || lower.startsWith('plan:') || lower === 'p' || lower === 'plano') {
        current = 'plan';
        const r = line.split(':').slice(1).join(':').trim();
        if (r) result.plan = r;
        continue;
      }
      if (lower.startsWith('diagnóstico:') || lower.startsWith('diagnostico:') || lower.startsWith('diagnosis:')) {
        result.diagnosis = line.split(':').slice(1).join(':').trim();
        current = null;
        continue;
      }
      if (lower.startsWith('cid:') || lower.startsWith('cid-10:') || lower.startsWith('código cid:')) {
        result.diagnosis_code = line
          .split(':')
          .slice(1)
          .join(':')
          .trim()
          .replace(/[^A-Z0-9.]/gi, '')
          .substring(0, 20);
        current = null;
        continue;
      }
      if (lower.startsWith('observações:') || lower.startsWith('observacoes:') || lower.startsWith('notas:')) {
        current = 'internal_notes';
        const r = line.split(':').slice(1).join(':').trim();
        if (r) result.internal_notes = r;
        continue;
      }
      if (lower.startsWith('retorno:') || lower.startsWith('follow-up:') || lower.startsWith('seguimento:')) {
        result.follow_up_notes = line.split(':').slice(1).join(':').trim();
        current = null;
        continue;
      }
      if (current && line) result[current] += (result[current] ? '\n' : '') + line;
    }
    return result;
  };

  const handleAiGenerate = async () => {
    if (!aiComplaint.trim()) {
      setAiError('Informe a queixa principal');
      return;
    }
    setAiGenerating(true);
    setAiError('');
    try {
      const res = await axios.post(getApiUrl('/clinic/evolutions/ai-generate'), {
        patient_name: patient?.full_name || '',
        complaint: aiComplaint.trim(),
        medical_history: aiMedicalHistory.trim(),
      });
      const aiData = res.data?.data;
      if (!aiData) throw new Error('Resposta vazia da IA');
      const rawText =
        typeof aiData === 'string'
          ? aiData
          : (aiData.output || aiData.text || aiData.response || aiData.evolution || JSON.stringify(aiData));
      const parsed = parseAiEvolution(rawText);
      setForm((prev: any) => ({
        ...prev,
        evolution_entry_format: 'free_text',
        evolution_free_text: [
          parsed.subjective?.trim() && `Subjetivo\n${parsed.subjective.trim()}`,
          parsed.objective?.trim() && `Objetivo\n${parsed.objective.trim()}`,
          parsed.assessment?.trim() && `Avaliação\n${parsed.assessment.trim()}`,
          parsed.plan?.trim() && `Plano\n${parsed.plan.trim()}`,
        ]
          .filter(Boolean)
          .join('\n\n'),
        subjective: '',
        objective: '',
        assessment: '',
        plan: '',
        diagnosis: parsed.diagnosis || prev.diagnosis,
        diagnosis_code: parsed.diagnosis_code || prev.diagnosis_code,
        internal_notes: parsed.internal_notes || prev.internal_notes,
        follow_up_notes: parsed.follow_up_notes || prev.follow_up_notes,
      }));
      setAiDialogOpen(false);
      setAiSnackbar({ open: true, message: 'Evolução gerada com IA! Revise os campos antes de concluir.', severity: 'success' });
    } catch (err: any) {
      setAiError(err.response?.data?.error || err.message || 'Erro ao gerar evolução com IA');
    } finally {
      setAiGenerating(false);
    }
  };

  const handleSignExisting = async () => {
    if (!existingEvolutionId) return;
    if (!window.confirm('Deseja assinar esta evolução? Após assinada, não poderá ser alterada como rascunho.')) return;
    try {
      setSigning(true);
      await axios.patch(getApiUrl(`/clinic/evolutions/${existingEvolutionId}/sign`));
      const { data: fresh } = await axios.get(getApiUrl(`/clinic/evolutions/${existingEvolutionId}`));
      setLoadedEvolutionSnapshot(fresh);
      setForm((prev: any) => ({
        ...prev,
        ...fresh,
        evolution_entry_format: 'free_text',
        evolution_date: fresh.evolution_date?.split('T')[0] || prev.evolution_date,
        follow_up_date: fresh.follow_up_date?.split?.('T')?.[0] ?? prev.follow_up_date ?? '',
      }));
      setAiSnackbar({ open: true, message: 'Evolução assinada com sucesso!', severity: 'success' });
      onSaved?.();
    } catch (err: any) {
      console.error(err);
      onError?.(err?.response?.data?.error || 'Erro ao assinar evolução');
    } finally {
      setSigning(false);
    }
  };

  const handleSave = async () => {
    if (!form.patient_id || !form.professional_id) {
      onError?.('Selecione paciente e profissional.');
      return;
    }
    try {
      setSaving(true);
      const payload: Record<string, any> = { ...form };
      delete payload.appointment_id;
      if (!payload.follow_up_date) delete payload.follow_up_date;

      const vs = payload.vital_signs || {};
      if (vs.weight && vs.height) {
        const h = vs.height / 100;
        vs.bmi = Math.round((vs.weight / (h * h)) * 10) / 10;
      }
      payload.vital_signs = vs;

      if (!existingEvolutionId) {
        const now = new Date();
        payload.evolution_date = now.toISOString().split('T')[0];
        payload.evolution_time = now.toTimeString().slice(0, 5);
      }

      payload.evolution_entry_format = 'free_text';
      payload.subjective = null;
      payload.objective = null;
      payload.assessment = null;
      payload.plan = null;

      payload.procedures_performed = procedureLabels.map((name) => ({ name, notes: '' }));
      payload.metadata = {
        ...(form.metadata || {}),
        evolution_workflow: {
          printables: attachedPrintables,
          return: returnWorkflow,
        },
      };

      if (existingEvolutionId) {
        await axios.put(getApiUrl(`/clinic/evolutions/${existingEvolutionId}`), payload);
        const { data: fresh } = await axios.get(getApiUrl(`/clinic/evolutions/${existingEvolutionId}`));
        setLoadedEvolutionSnapshot(fresh);
      } else {
        await axios.post(getApiUrl('/clinic/evolutions'), payload);
      }
      onSaved?.();
      if (!embedded) {
        onClose();
      } else {
        setAiSnackbar({
          open: true,
          message: 'Evolução salva no prontuário. Você pode continuar na chamada.',
          severity: 'success',
        });
      }
    } catch (err: any) {
      console.error(err);
      onError?.(err?.response?.data?.error || 'Erro ao salvar evolução');
    } finally {
      setSaving(false);
    }
  };

  if (!patient) return null;

  const initialProfessional =
    profOptions.find((p: any) => p.id === form.professional_id) ||
    getLoggedInProfessionalOption(user);

  return (
    <>
      <Dialog
        open={open}
        onClose={onClose}
        maxWidth={embedded ? false : 'lg'}
        fullWidth={!embedded}
        fullScreen={embedded ? false : isMobile}
        disablePortal={embedded}
        hideBackdrop={embedded}
        disableEscapeKeyDown={embedded}
        scroll="paper"
        PaperProps={{
          sx: embedded
            ? {
                m: 0,
                width: '100%',
                height: '100%',
                maxHeight: '100%',
                borderRadius: 0,
                display: 'flex',
                flexDirection: 'column',
                overflow: 'hidden',
              }
            : isMobile
              ? { m: 0, maxHeight: '100%', borderRadius: 0, height: '100%', display: 'flex', flexDirection: 'column' }
              : { borderRadius: 3, maxHeight: '95vh' },
        }}
        sx={
          embedded
            ? {
                position: 'static',
                flex: 1,
                alignSelf: 'stretch',
                minHeight: 0,
                display: 'flex',
                flexDirection: 'column',
                '& .MuiDialog-container': {
                  height: '100%',
                  alignItems: 'stretch',
                  justifyContent: 'stretch',
                },
              }
            : undefined
        }
      >
        <DialogTitle
          sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', pb: 1, flexShrink: 0, px: { xs: 2, sm: 3 } }}
        >
          <Box sx={{ minWidth: 0 }}>
            <Typography variant="h6" sx={{ fontWeight: 700 }}>
              {existingEvolutionId ? (isSignedState ? 'Evolução (assinada)' : 'Editar evolução') : 'Nova evolução clínica'}
            </Typography>
            <Typography variant="caption" sx={{ color: '#64748b' }}>
              {patient.full_name}
              {!existingEvolutionId
                ? ' — mesmo fluxo da página Evoluções (texto livre, impressos e retorno)'
                : ' — aberto aqui mesmo (sem mudar de página)'}
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexShrink: 0 }}>
            {existingEvolutionId && loadedEvolutionSnapshot && !isSignedState && (
              <Button
                size="small"
                variant="contained"
                color="success"
                disabled={signing || loadingExisting}
                onClick={() => void handleSignExisting()}
                sx={{ textTransform: 'none', fontWeight: 700, fontSize: 12, mr: 0.25 }}
              >
                {signing ? 'Assinando…' : 'Assinar'}
              </Button>
            )}
            {!existingEvolutionId && (
            <Button
              size="small"
              startIcon={<AiIcon />}
              onClick={() => {
                setAiComplaint('');
                setAiMedicalHistory('');
                setAiError('');
                setAiDialogOpen(true);
              }}
              sx={{
                background: 'linear-gradient(135deg, #8b5cf6 0%, #6d28d9 100%)',
                color: '#fff',
                borderRadius: 2,
                textTransform: 'none',
                fontWeight: 600,
                fontSize: 12,
                px: 1.5,
                '&:hover': { background: 'linear-gradient(135deg, #7c3aed 0%, #5b21b6 100%)' },
              }}
            >
              Gerar com IA
            </Button>
            )}
            {!embedded && (
            <IconButton onClick={onClose} aria-label="Fechar" size="small">
              <CloseIcon />
            </IconButton>
            )}
          </Box>
        </DialogTitle>
        <Divider />

        <DialogContent sx={{ pt: 2, flex: 1, overflow: 'auto', px: { xs: 2, sm: 3 }, pb: 1 }}>
          {loadingExisting ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 320 }}>
              <CircularProgress />
            </Box>
          ) : (
            <>
          <Accordion
            defaultExpanded
            sx={{
              mb: 2,
              borderRadius: 2,
              '&:before': { display: 'none' },
              boxShadow: '0 1px 3px rgba(0,0,0,0.06)',
              border: '1px solid',
              borderColor: 'divider',
            }}
          >
            <AccordionSummary expandIcon={<ExpandMoreAccordionIcon />} sx={{ px: 2, minHeight: 48, '& .MuiAccordionSummary-content': { my: 1 } }}>
              <Box>
                <Typography sx={{ fontWeight: 700, fontSize: '0.95rem' }}>Identificação do atendimento</Typography>
                <Typography variant="caption" sx={{ color: '#64748b' }}>
                  Paciente e profissional responsável
                </Typography>
              </Box>
            </AccordionSummary>
            <AccordionDetails sx={{ pt: 0, px: 2, pb: 2 }}>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <TextField fullWidth size="small" label="Paciente" value={patient.full_name} disabled />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Autocomplete
                    size="small"
                    disabled={contentDisabled}
                    options={profOptions}
                    getOptionLabel={(o: any) => `${o.full_name || ''}${o.specialty ? ` - ${o.specialty}` : ''}`}
                    onInputChange={(_, v, r) => {
                      if (r !== 'reset') setProfSearch(v);
                    }}
                    onChange={(_, v) => setForm({ ...form, professional_id: v?.id || '' })}
                    value={profOptions.find((p: any) => p.id === form.professional_id) || null}
                    renderInput={(params) => <TextField {...params} label="Profissional *" />}
                    isOptionEqualToValue={(o: any, v: any) => o.id === v.id}
                    noOptionsText="Nenhum profissional encontrado"
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    size="small"
                    disabled={contentDisabled}
                    label="Diagnóstico"
                    value={form.diagnosis}
                    onChange={(e) => setForm({ ...form, diagnosis: e.target.value })}
                    placeholder="Opcional — ex.: catarata nuclear"
                  />
                </Grid>
              </Grid>
            </AccordionDetails>
          </Accordion>

          <Paper
            elevation={0}
            sx={{
              mb: 2,
              border: '1px solid',
              borderColor: 'divider',
              borderRadius: 2,
              overflow: 'hidden',
            }}
          >
            <Box
              sx={{
                px: 2,
                py: 1.25,
                bgcolor: '#f0fdfa',
                borderBottom: '1px solid',
                borderColor: 'divider',
              }}
            >
              <Typography variant="overline" sx={{ fontWeight: 800, color: '#0f766e', letterSpacing: '0.12em' }}>
                Evolução
              </Typography>
            </Box>
            <Box sx={{ p: 2 }}>
              <TextField
                fullWidth
                multiline
                minRows={12}
                maxRows={32}
                disabled={contentDisabled}
                value={form.evolution_free_text}
                onChange={(e) => setForm({ ...form, evolution_free_text: e.target.value })}
                placeholder="Evolução em texto livre..."
                size="small"
              />
            </Box>
          </Paper>

          <Paper
            elevation={0}
            sx={{
              mb: 2,
              border: '1px solid',
              borderColor: 'divider',
              borderRadius: 2,
              overflow: 'hidden',
            }}
          >
            <Box
              onClick={() => {
                if (contentDisabled) return;
                setPrintWizardOpen(true);
              }}
              onKeyDown={(e) => {
                if (contentDisabled) return;
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  setPrintWizardOpen(true);
                }
              }}
              role="button"
              tabIndex={contentDisabled ? -1 : 0}
              sx={{
                px: 2,
                py: 1.5,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                gap: 2,
                cursor: contentDisabled ? 'default' : 'pointer',
                bgcolor: '#fafafa',
                borderBottom: '1px solid',
                borderColor: 'divider',
                opacity: contentDisabled ? 0.7 : 1,
                '&:hover': { bgcolor: contentDisabled ? '#fafafa' : '#f1f5f9' },
              }}
            >
              <Box sx={{ minWidth: 0 }}>
                <Typography sx={{ fontWeight: 800, fontSize: '0.8rem', letterSpacing: '0.06em', color: '#0f172a' }}>
                  IMPRESSOS E SOLICITAÇÕES
                </Typography>
                <Typography variant="caption" sx={{ color: '#64748b' }}>
                  Receituário, atestado, pedido de exame, laudo ou risco cirúrgico (para cirurgias e procedimentos, use a área Indicações logo abaixo)
                </Typography>
              </Box>
              <Button
                size="small"
                variant="outlined"
                startIcon={<NoteAddIcon />}
                disabled={contentDisabled}
                onClick={(e) => {
                  e.stopPropagation();
                  if (contentDisabled) return;
                  setPrintWizardOpen(true);
                }}
                sx={{ textTransform: 'none', flexShrink: 0, borderColor: '#cbd5e1', color: '#334155' }}
              >
                Adicionar
              </Button>
            </Box>
            <Box sx={{ p: 2 }}>
              <Typography variant="caption" sx={{ color: '#64748b', fontWeight: 600 }}>
                Materiais selecionados
              </Typography>
              <Box
                sx={{
                  mt: 1.25,
                  minHeight: 48,
                  maxHeight: 168,
                  overflow: 'auto',
                  display: 'flex',
                  flexWrap: 'wrap',
                  gap: 1,
                  alignContent: 'flex-start',
                }}
              >
                {attachedPrintables.length === 0 ? (
                  <Typography variant="body2" sx={{ color: '#94a3b8', fontStyle: 'italic' }}>
                    Nenhum documento anexado. Use o cabeçalho acima para lançar modelos cadastrados na clínica.
                  </Typography>
                ) : (
                  attachedPrintables.map((p) => (
                    <Paper
                      key={p.clientKey}
                      variant="outlined"
                      sx={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: 0.25,
                        pl: 1.25,
                        pr: 0.25,
                        py: 0.25,
                        borderRadius: 10,
                        borderColor: '#e2e8f0',
                        bgcolor: '#fff',
                      }}
                    >
                      <Typography variant="body2" noWrap sx={{ maxWidth: { xs: 160, sm: 240 }, fontWeight: 600 }}>
                        {p.category === 'prescription' ? evolutionPrintableListLabel(p) : p.templateName}
                      </Typography>
                      {p.category !== 'prescription' ? (
                      <Typography variant="caption" sx={{ color: '#94a3b8', display: { xs: 'none', sm: 'inline' }, mr: 0.5 }}>
                        {printableCategoryLabel[p.category]}
                      </Typography>
                      ) : null}
                      <Tooltip title="Imprimir este documento">
                        <IconButton size="small" onClick={() => void printAttachedDocuments([p])} aria-label="Imprimir">
                          <PrintIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      {!contentDisabled && (
                        <Tooltip title="Remover">
                          <IconButton
                            size="small"
                            onClick={() => setAttachedPrintables((prev) => prev.filter((x) => x.clientKey !== p.clientKey))}
                            aria-label="Remover"
                          >
                            <CloseIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      )}
                    </Paper>
                  ))
                )}
              </Box>
            </Box>
          </Paper>

          <Paper
            elevation={0}
            sx={{
              mb: 2,
              border: '1px solid',
              borderColor: 'divider',
              borderRadius: 2,
              overflow: 'hidden',
            }}
          >
            <Box
              onClick={() => {
                if (contentDisabled) return;
                setIndicationsDialogOpen(true);
              }}
              onKeyDown={(e) => {
                if (contentDisabled) return;
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  setIndicationsDialogOpen(true);
                }
              }}
              role="button"
              tabIndex={contentDisabled ? -1 : 0}
              sx={{
                px: 2,
                py: 1.5,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                gap: 2,
                cursor: contentDisabled ? 'default' : 'pointer',
                bgcolor: '#fafafa',
                borderBottom: '1px solid',
                borderColor: 'divider',
                opacity: contentDisabled ? 0.7 : 1,
                '&:hover': { bgcolor: contentDisabled ? '#fafafa' : '#f1f5f9' },
              }}
            >
              <Box>
                <Typography sx={{ fontWeight: 800, fontSize: '0.8rem', letterSpacing: '0.06em', color: '#0f172a' }}>
                  INDICAÇÕES
                </Typography>
                <Typography variant="caption" sx={{ color: '#64748b' }}>
                  Cirurgias e procedimentos. Use Adicionar para abrir o seletor (cadastro ou grade OD/OE/AO em oftalmologia).
                </Typography>
              </Box>
              <Button
                size="small"
                variant="outlined"
                startIcon={<NoteAddIcon />}
                disabled={contentDisabled}
                onClick={(e) => {
                  e.stopPropagation();
                  if (contentDisabled) return;
                  setIndicationsDialogOpen(true);
                }}
                sx={{ textTransform: 'none', flexShrink: 0, borderColor: '#cbd5e1', color: '#334155' }}
              >
                Adicionar
              </Button>
            </Box>
            <Box sx={{ p: 2 }}>
              <Typography variant="caption" sx={{ color: '#64748b', fontWeight: 600 }}>
                Cirurgias e procedimentos selecionados
              </Typography>
              <Box
                sx={{
                  mt: 1.25,
                  minHeight: 44,
                  maxHeight: 160,
                  overflow: 'auto',
                  display: 'flex',
                  flexWrap: 'wrap',
                  gap: 1,
                }}
              >
                {procedureLabels.length === 0 ? (
                  <Typography variant="body2" sx={{ color: '#94a3b8', fontStyle: 'italic' }}>
                    Nenhuma indicação registrada.
                  </Typography>
                ) : (
                  procedureLabels.map((label, idx) => (
                    <Paper
                      key={`proc-${idx}-${label}`}
                      variant="outlined"
                      sx={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: 0.25,
                        pl: 1.25,
                        pr: 0.25,
                        py: 0.25,
                        borderRadius: 10,
                        borderColor: '#e2e8f0',
                      }}
                    >
                      <Typography variant="body2" noWrap sx={{ maxWidth: { xs: 180, sm: 280 }, fontWeight: 600 }}>
                        {label}
                      </Typography>
                      <Tooltip title="Imprimir indicação">
                        <IconButton size="small" onClick={() => void printProcedureLabel(label)} aria-label="Imprimir">
                          <PrintIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      {!contentDisabled && (
                        <Tooltip title="Remover">
                          <IconButton
                            size="small"
                            onClick={() => setProcedureLabels((prev) => prev.filter((x) => x !== label))}
                            aria-label="Remover"
                          >
                            <CloseIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      )}
                    </Paper>
                  ))
                )}
              </Box>
            </Box>
          </Paper>

          <Paper
            elevation={0}
            sx={{
              mb: 2,
              border: '1px solid',
              borderColor: 'divider',
              borderRadius: 2,
              overflow: 'hidden',
            }}
          >
            <Box sx={{ px: 2, py: 1.25, bgcolor: '#f0fdfa', borderBottom: '1px solid', borderColor: 'divider' }}>
              <Typography variant="overline" sx={{ fontWeight: 800, color: '#0f766e', letterSpacing: '0.12em' }}>
                Retorno
              </Typography>
            </Box>
            <Box sx={{ p: 2 }}>
              <FormGroup>
                <FormControlLabel
                  control={
                    <Checkbox
                      size="small"
                      disabled={contentDisabled}
                      checked={returnWorkflow.returnWithExams}
                      onChange={(e) => setReturnWorkflow((w) => ({ ...w, returnWithExams: e.target.checked }))}
                      sx={{ color: '#14B8A6', '&.Mui-checked': { color: '#14B8A6' } }}
                    />
                  }
                  label="Retorno com exames"
                />
                <FormControlLabel
                  control={
                    <Checkbox
                      size="small"
                      disabled={contentDisabled}
                      checked={returnWorkflow.returnForSurgery}
                      onChange={(e) => setReturnWorkflow((w) => ({ ...w, returnForSurgery: e.target.checked }))}
                      sx={{ color: '#14B8A6', '&.Mui-checked': { color: '#14B8A6' } }}
                    />
                  }
                  label="Retorno para cirurgia"
                />
                <FormControlLabel
                  control={
                    <Checkbox
                      size="small"
                      disabled={contentDisabled}
                      checked={returnWorkflow.dischargeNoRecall}
                      onChange={(e) => setReturnWorkflow((w) => ({ ...w, dischargeNoRecall: e.target.checked }))}
                      sx={{ color: '#14B8A6', '&.Mui-checked': { color: '#14B8A6' } }}
                    />
                  }
                  label="Alta — não reconvocar"
                />
              </FormGroup>
              <Grid container spacing={2} sx={{ mt: 0.5 }}>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    size="small"
                    disabled={contentDisabled}
                    label="Retorno em (dias)"
                    type="number"
                    value={returnWorkflow.returnInDays}
                    onChange={(e) => setReturnWorkflow((w) => ({ ...w, returnInDays: e.target.value }))}
                    inputProps={{ min: 0 }}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    size="small"
                    disabled={contentDisabled}
                    label="Reavaliação em (dias)"
                    type="number"
                    value={returnWorkflow.reevaluationInDays}
                    onChange={(e) => setReturnWorkflow((w) => ({ ...w, reevaluationInDays: e.target.value }))}
                    inputProps={{ min: 0 }}
                  />
                </Grid>
              </Grid>
            </Box>
          </Paper>

          <Accordion
            defaultExpanded={false}
            sx={{
              mb: 1,
              borderRadius: 2,
              '&:before': { display: 'none' },
              boxShadow: '0 1px 3px rgba(0,0,0,0.06)',
              border: '1px solid',
              borderColor: 'divider',
            }}
          >
            <AccordionSummary expandIcon={<ExpandMoreAccordionIcon />} sx={{ px: 2 }}>
              <Box>
                <Typography sx={{ fontWeight: 700, fontSize: '0.95rem' }}>Sinais vitais e complementar</Typography>
                <Typography variant="caption" sx={{ color: '#64748b' }}>
                  PA, FC, peso, notas internas e retorno em texto livre
                </Typography>
              </Box>
            </AccordionSummary>
            <AccordionDetails sx={{ px: 2, pb: 2, pt: 0 }}>
              <Alert severity="info" sx={{ mb: 2, borderRadius: 2 }}>
                Preencha apenas o que foi aferido. IMC calculado quando peso e altura existirem.
              </Alert>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6} md={4}>
                  <TextField
                    fullWidth
                    size="small"
                    disabled={contentDisabled}
                    label="PA sistólica (mmHg)"
                    type="number"
                    value={form.vital_signs?.blood_pressure_systolic || ''}
                    onChange={(e) => updateVital('blood_pressure_systolic', e.target.value)}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <HeartIcon sx={{ color: '#ef4444', fontSize: 20 }} />
                        </InputAdornment>
                      ),
                    }}
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={4}>
                  <TextField
                    fullWidth
                    size="small"
                    disabled={contentDisabled}
                    label="PA diastólica (mmHg)"
                    type="number"
                    value={form.vital_signs?.blood_pressure_diastolic || ''}
                    onChange={(e) => updateVital('blood_pressure_diastolic', e.target.value)}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <HeartIcon sx={{ color: '#ef4444', fontSize: 20 }} />
                        </InputAdornment>
                      ),
                    }}
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={4}>
                  <TextField
                    fullWidth
                    size="small"
                    disabled={contentDisabled}
                    label="FC (bpm)"
                    type="number"
                    value={form.vital_signs?.heart_rate || ''}
                    onChange={(e) => updateVital('heart_rate', e.target.value)}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <SpeedIcon sx={{ color: '#f59e0b', fontSize: 20 }} />
                        </InputAdornment>
                      ),
                    }}
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={4}>
                  <TextField
                    fullWidth
                    size="small"
                    disabled={contentDisabled}
                    label="FR (irpm)"
                    type="number"
                    value={form.vital_signs?.respiratory_rate || ''}
                    onChange={(e) => updateVital('respiratory_rate', e.target.value)}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <LungIcon sx={{ color: '#3b82f6', fontSize: 20 }} />
                        </InputAdornment>
                      ),
                    }}
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={4}>
                  <TextField
                    fullWidth
                    size="small"
                    disabled={contentDisabled}
                    label="Temperatura (°C)"
                    type="number"
                    value={form.vital_signs?.temperature || ''}
                    onChange={(e) => updateVital('temperature', e.target.value)}
                    inputProps={{ step: 0.1 }}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <TempIcon sx={{ color: '#ef4444', fontSize: 20 }} />
                        </InputAdornment>
                      ),
                    }}
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={4}>
                  <TextField
                    fullWidth
                    size="small"
                    disabled={contentDisabled}
                    label="SpO₂ (%)"
                    type="number"
                    value={form.vital_signs?.oxygen_saturation || ''}
                    onChange={(e) => updateVital('oxygen_saturation', e.target.value)}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <BloodIcon sx={{ color: '#8b5cf6', fontSize: 20 }} />
                        </InputAdornment>
                      ),
                    }}
                  />
                </Grid>
                <Grid item xs={12}>
                  <Divider sx={{ my: 0.5 }} />
                </Grid>
                <Grid item xs={12} sm={4}>
                  <TextField
                    fullWidth
                    size="small"
                    disabled={contentDisabled}
                    label="Peso (kg)"
                    type="number"
                    value={form.vital_signs?.weight || ''}
                    onChange={(e) => updateVital('weight', e.target.value)}
                    inputProps={{ step: 0.1 }}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <WeightIcon sx={{ color: '#14B8A6', fontSize: 20 }} />
                        </InputAdornment>
                      ),
                    }}
                  />
                </Grid>
                <Grid item xs={12} sm={4}>
                  <TextField
                    fullWidth
                    size="small"
                    disabled={contentDisabled}
                    label="Altura (cm)"
                    type="number"
                    value={form.vital_signs?.height || ''}
                    onChange={(e) => updateVital('height', e.target.value)}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <HeightIcon sx={{ color: '#14B8A6', fontSize: 20 }} />
                        </InputAdornment>
                      ),
                    }}
                  />
                </Grid>
                <Grid item xs={12} sm={4}>
                  <TextField
                    fullWidth
                    size="small"
                    label="IMC"
                    type="number"
                    disabled
                    value={
                      form.vital_signs?.weight && form.vital_signs?.height
                        ? (form.vital_signs.weight / Math.pow(form.vital_signs.height / 100, 2)).toFixed(1)
                        : ''
                    }
                    helperText={(() => {
                      if (!form.vital_signs?.weight || !form.vital_signs?.height) {
                        return 'Calculado automaticamente';
                      }
                      const bmi = form.vital_signs.weight / Math.pow(form.vital_signs.height / 100, 2);
                      if (bmi < 18.5) return 'Abaixo do peso';
                      if (bmi < 25) return 'Peso normal';
                      if (bmi < 30) return 'Sobrepeso';
                      return 'Obesidade';
                    })()}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    size="small"
                    disabled={contentDisabled}
                    label="Glicemia (mg/dL)"
                    type="number"
                    value={form.vital_signs?.blood_glucose || ''}
                    onChange={(e) => updateVital('blood_glucose', e.target.value)}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    size="small"
                    disabled={contentDisabled}
                    label="Escala de dor (0–10)"
                    type="number"
                    value={form.vital_signs?.pain_scale ?? ''}
                    onChange={(e) => updateVital('pain_scale', e.target.value)}
                    inputProps={{ min: 0, max: 10 }}
                    helperText={(() => {
                      const pVal = form.vital_signs?.pain_scale;
                      if (pVal === undefined || pVal === null || pVal === '') return '';
                      if (pVal <= 3) return 'Dor leve';
                      if (pVal <= 6) return 'Dor moderada';
                      return 'Dor intensa';
                    })()}
                  />
                </Grid>
                <Grid item xs={12}>
                  <Divider sx={{ my: 1 }} />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    size="small"
                    disabled={contentDisabled}
                    multiline
                    minRows={2}
                    maxRows={10}
                    label="Observações internas"
                    value={form.internal_notes}
                    onChange={(e) => setForm({ ...form, internal_notes: e.target.value })}
                    placeholder="Notas para a equipe (não destinadas ao paciente)"
                    helperText="Visível apenas na clínica"
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    size="small"
                    disabled={contentDisabled}
                    multiline
                    minRows={2}
                    maxRows={10}
                    label="Orientações de retorno (texto livre)"
                    value={form.follow_up_notes}
                    onChange={(e) => setForm({ ...form, follow_up_notes: e.target.value })}
                    placeholder="Combine com as opções de retorno acima, se desejar"
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    size="small"
                    disabled={contentDisabled}
                    type="date"
                    label="Data prevista de retorno"
                    value={form.follow_up_date}
                    onChange={(e) => setForm({ ...form, follow_up_date: e.target.value })}
                    InputLabelProps={{ shrink: true }}
                  />
                </Grid>
              </Grid>
            </AccordionDetails>
          </Accordion>
            </>
          )}
        </DialogContent>

        <DialogActions
          sx={{
            px: { xs: 2, sm: 3 },
            py: 2,
            flexShrink: 0,
            flexWrap: 'wrap',
            gap: 2,
            justifyContent: 'space-between',
            alignItems: 'center',
            borderTop: '1px solid',
            borderColor: 'divider',
            bgcolor: '#fafafa',
          }}
        >
          <Box
            sx={{
              px: 1.5,
              py: 0.75,
              borderRadius: 2,
              bgcolor: isSignedState ? '#ecfdf5' : '#fffbeb',
              border: '1px solid',
              borderColor: isSignedState ? '#bbf7d0' : '#fcd34d',
              maxWidth: { xs: '100%', sm: 420 },
            }}
          >
            <Typography variant="caption" sx={{ color: isSignedState ? '#166534' : '#92400e', fontWeight: 600 }}>
              {isSignedState
                ? 'Evolução assinada: conteúdo bloqueado. Use impressão para documento completo ou anexos.'
                : existingEvolutionId
                  ? 'Rascunho: alterações ao «Concluir». Também pode assinar pelo botão «Assinar» quando terminar.'
                  : 'Ao concluir, a evolução é gravada no prontuário com data e hora atuais.'}
            </Typography>
          </Box>
          <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap" justifyContent="flex-end" sx={{ width: { xs: '100%', sm: 'auto' } }}>
            <Button onClick={onClose} sx={{ color: '#64748b', textTransform: 'none' }}>
              {isSignedState ? 'Fechar' : embedded ? 'Ocultar painel' : 'Cancelar'}
            </Button>
            {existingEvolutionId && loadedEvolutionSnapshot && (
              <Button
                variant="outlined"
                startIcon={<PrintIcon />}
                onClick={() => void printClinicalEvolutionFullDocument(loadedEvolutionSnapshot)}
                sx={{ textTransform: 'none', borderColor: '#3b82f6', color: '#1d4ed8' }}
              >
                Documento completo
              </Button>
            )}
            <Button
              variant="outlined"
              disabled={!evolutionHasPrintBatchContent(attachedPrintables, procedureLabels)}
              startIcon={<PrintIcon />}
              onClick={() => void printAttachedDocuments(buildEvolutionPrintBatch(attachedPrintables, procedureLabels))}
              sx={{ textTransform: 'none', borderColor: '#cbd5e1', color: '#334155' }}
            >
              Imprimir todos
            </Button>
            <Button
              variant="contained"
              onClick={handleSave}
              disabled={saving || contentDisabled}
              startIcon={saving ? <CircularProgress size={16} color="inherit" /> : null}
              sx={{
                minWidth: 140,
                fontWeight: 800,
                letterSpacing: 0.06,
                textTransform: 'none',
                bgcolor: '#14B8A6',
                '&:hover': { bgcolor: '#0d9488' },
              }}
            >
              {saving ? 'Salvando…' : 'Concluir'}
            </Button>
          </Stack>
        </DialogActions>
      </Dialog>

      <EvolutionPrintablesWizard
        open={printWizardOpen}
        onClose={() => setPrintWizardOpen(false)}
        patientId={form.patient_id}
        professionalId={form.professional_id}
        initialPatient={patient}
        initialProfessional={initialProfessional}
        onCommit={(item) => {
          setAttachedPrintables((prev) => [...prev, item]);
        }}
      />
      <EvolutionIndicationsDialog
        open={indicationsDialogOpen}
        onClose={() => setIndicationsDialogOpen(false)}
        onFinish={({ labels, printable }) => {
          setProcedureLabels((prev) => {
            const next = new Set(prev);
            for (const l of labels || []) {
              const t = String(l || '').trim();
              if (t) next.add(t);
            }
            return Array.from(next);
          });
          if (printable)
            setAttachedPrintables((prev) => [...prev, printable]);
        }}
      />

      <Dialog
        open={aiDialogOpen}
        onClose={() => !aiGenerating && setAiDialogOpen(false)}
        maxWidth="sm"
        fullWidth
        fullScreen={isMobile}
        PaperProps={{ sx: { borderRadius: isMobile ? 0 : 3, overflow: 'hidden', m: isMobile ? 0 : 2 } }}
      >
        <Box
          sx={{
            background: 'linear-gradient(135deg, #8b5cf6 0%, #6d28d9 100%)',
            px: 3,
            py: 2,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <AiIcon sx={{ color: '#fff' }} />
            <Typography variant="h6" sx={{ color: '#fff', fontWeight: 700 }}>
              Gerar evolução com IA
            </Typography>
          </Box>
          <IconButton onClick={() => !aiGenerating && setAiDialogOpen(false)} sx={{ color: '#fff' }}>
            <CloseIcon />
          </IconButton>
        </Box>
        <DialogContent sx={{ pt: 3 }}>
          <Typography variant="body2" sx={{ color: '#64748b', mb: 2 }}>
            A evolução será gerada em <strong>texto livre</strong> para <strong>{patient.full_name}</strong>. Revise antes de concluir.
          </Typography>
          {aiError && (
            <Alert severity="error" sx={{ mb: 2, borderRadius: 2 }}>
              {aiError}
            </Alert>
          )}
          <TextField
            fullWidth
            label="Queixa principal / motivo da consulta"
            value={aiComplaint}
            onChange={(e) => setAiComplaint(e.target.value)}
            sx={{ mb: 2 }}
            multiline
            rows={2}
            placeholder="Ex.: dor lombar há 3 dias com irradiação para membro inferior esquerdo"
            disabled={aiGenerating}
          />
          <TextField
            fullWidth
            label="Histórico médico / informações adicionais (opcional)"
            value={aiMedicalHistory}
            onChange={(e) => setAiMedicalHistory(e.target.value)}
            multiline
            rows={3}
            placeholder="Ex.: HAS, DM2, uso contínuo de Metformina 850 mg"
            disabled={aiGenerating}
          />
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5, gap: 1 }}>
          <Button onClick={() => setAiDialogOpen(false)} disabled={aiGenerating} sx={{ color: '#64748b' }}>
            Cancelar
          </Button>
          <Button
            variant="contained"
            onClick={handleAiGenerate}
            disabled={aiGenerating}
            startIcon={aiGenerating ? <CircularProgress size={18} sx={{ color: '#fff' }} /> : <AiIcon />}
            sx={{
              background: 'linear-gradient(135deg, #8b5cf6 0%, #6d28d9 100%)',
              '&:hover': { background: 'linear-gradient(135deg, #7c3aed 0%, #5b21b6 100%)' },
              borderRadius: 2,
              textTransform: 'none',
              fontWeight: 600,
              px: 3,
            }}
          >
            {aiGenerating ? 'Gerando…' : 'Gerar com IA'}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={aiSnackbar.open}
        autoHideDuration={4000}
        onClose={() => setAiSnackbar({ ...aiSnackbar, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert
          onClose={() => setAiSnackbar({ ...aiSnackbar, open: false })}
          severity={aiSnackbar.severity}
          sx={{ borderRadius: 2 }}
        >
          {aiSnackbar.message}
        </Alert>
      </Snackbar>
    </>
  );
};

export default HealthcareEvolutionDialog;
